import axios from 'axios';
import { term } from 'lucene';
import type {
  MaterialV1,
  CreateMaterialV1Request,
  UpdateMaterialContentRequest,
} from '@ilteducation/content-registry-types';
import store from '@/store';
import type { MaterialV1WithTmpFile, NewMaterialV1WithTmpFile } from '@/resources/materials/types';

const getHeaders = () => ({
  'Content-Type': 'application/json',
});

const setLoading = (isLoading: boolean): void => {
  store.commit('api/setLoading', isLoading);
};

export const suggestOriginalTitles = async (suggest?: string): Promise<string[]> => {
  if (!suggest) {
    return [];
  }
  const escapedSuggest = term.escape(suggest);

  const response = await axios.get(`${process.env.VUE_APP_MATERIALS_API_URL_SUGGEST}`, {
    headers: getHeaders(),
    params: { originalTitle: escapedSuggest },
  });
  return response.data;
};

export const getOriginalTitleRelated = async (material: MaterialV1): Promise<MaterialV1[]> => {
  if (!material.originalTitle) {
    return [];
  }

  const escapedOriginalTitle = term.escape(material.originalTitle);
  const response = await axios.get(`${process.env.VUE_APP_MATERIALS_API_URL_LIST}`, {
    headers: getHeaders(),
    params: {
      filter: `originalTitle:${escapedOriginalTitle}`,
      'pagination[size]': 100,
    },
  });
  const { data } = response.data;
  return data.filter((m: MaterialV1) => m.id !== material.id);
};

export const createBulk = async (materials: NewMaterialV1WithTmpFile[]): Promise<Partial<MaterialV1>[]> => {
  setLoading(true);
  try {
    const promises = materials.map((material) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { tmpFile, ...rest } = material;
      const payload: CreateMaterialV1Request = rest;
      return axios
        .post(`${process.env.VUE_APP_MATERIALS_API_URL}`, payload, {
          headers: getHeaders(),
        })
        .then((response) => ({ ...material, id: response.data.id } as Partial<MaterialV1>));
    });
    return await Promise.all(promises);
  } finally {
    setLoading(false);
  }
};

export const updateBulk = async (materials: MaterialV1WithTmpFile[]): Promise<MaterialV1[]> => {
  setLoading(true);
  try {
    const promises = materials.map((material) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { id, state, file, tmpFile, createdAt, lastUpdatedAt, ...put } = material;
      if (!Object.keys(put).length) {
        console.debug('No changes for material', material.id);
        return { data: null };
      }
      const payload: UpdateMaterialContentRequest = put;
      return axios.put(`${process.env.VUE_APP_MATERIALS_API_URL}${material.id}`, payload, {
        headers: getHeaders(),
      });
    });
    const responses = await Promise.all(promises);
    return responses.filter(Boolean).map((response) => response.data as MaterialV1);
  } finally {
    setLoading(false);
  }
};
